import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { WholesalerDefinitionPublic } from 'src/app/models/invoice-import.model';
import { InvoiceImportService } from 'src/app/services/invoice-import.service';

@Component({
  selector: 'app-wholesaler-definitions-admin',
  templateUrl: './wholesaler-definitions-admin.component.html',
  styleUrls: ['./wholesaler-definitions-admin.component.scss'],
})
export class WholesalerDefinitionsAdminComponent implements OnInit {
  definitions: WholesalerDefinitionPublic[] = [];
  selected: WholesalerDefinitionPublic | null = null;

  displayName = '';
  description = '';
  isActive = true;
  approvedDomains = '';
  approvedAddresses = '';
  behaviorDefaultsJson = '{}';
  routingConfigJson = '{}';
  storeSetupSchemaJson = '{}';
  jsonError = '';
  loadError = '';

  constructor(
    private invoiceImportService: InvoiceImportService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const role = localStorage.getItem('role');
    if (role !== 'admin') {
      this.loadError = this.translate.instant('INVOICE_IMPORTS.ADMIN.FORBIDDEN');
      return;
    }
    this.load();
  }

  load(): void {
    this.spinner.show();
    this.invoiceImportService.listAdminWholesalerDefinitions().subscribe({
      next: (res: any) => {
        this.spinner.hide();
        const data = res.response?.data || res.data || {};
        this.definitions = data.definitions || [];
      },
      error: (err: any) => {
        this.spinner.hide();
        this.loadError = err.error?.response?.message || err.message;
      },
    });
  }

  select(def: WholesalerDefinitionPublic): void {
    this.selected = def;
    this.displayName = def.displayName || '';
    this.description = def.description || '';
    this.isActive = def.isActive !== false;
    this.approvedDomains = (def.senderConfig?.approvedDomains || []).join('\n');
    this.approvedAddresses = (def.senderConfig?.approvedAddresses || []).join('\n');
    this.behaviorDefaultsJson = JSON.stringify(def.behaviorDefaults || {}, null, 2);
    this.routingConfigJson = JSON.stringify(def.routingConfig || {}, null, 2);
    this.storeSetupSchemaJson = JSON.stringify(def.storeSetupSchema || {}, null, 2);
    this.jsonError = '';
  }

  save(): void {
    if (!this.selected) {
      return;
    }
    let behaviorDefaults: unknown;
    let routingConfig: unknown;
    let storeSetupSchema: unknown;
    try {
      behaviorDefaults = JSON.parse(this.behaviorDefaultsJson);
      routingConfig = JSON.parse(this.routingConfigJson);
      storeSetupSchema = JSON.parse(this.storeSetupSchemaJson);
      this.jsonError = '';
    } catch {
      this.jsonError = this.translate.instant('INVOICE_IMPORTS.ADMIN.JSON_INVALID');
      return;
    }

    const body = {
      displayName: this.displayName,
      description: this.description,
      isActive: this.isActive,
      senderConfig: {
        approvedDomains: this.approvedDomains.split('\n').map((s) => s.trim()).filter(Boolean),
        approvedAddresses: this.approvedAddresses.split('\n').map((s) => s.trim().toLowerCase()).filter(Boolean),
      },
      behaviorDefaults,
      routingConfig,
      storeSetupSchema,
    };

    this.spinner.show();
    this.invoiceImportService.putAdminWholesalerDefinition(this.selected.supplierKey, body as Record<string, unknown>).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        const data = res.response?.data || res.data || {};
        const updated = data.definition;
        if (updated) {
          this.select(updated);
        }
        this.toastr.success(this.translate.instant('INVOICE_IMPORTS.ADMIN.SAVED'));
        this.load();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }
}
